import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { ArrowLeft, X } from 'lucide-react-native';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useWindowDimensions } from 'react-native';
import Animated, { SlideInDown, SlideOutDown, FadeIn, FadeOut } from 'react-native-reanimated';

import { Box } from '@/components/ui/box';
import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { Icon } from '@/components/ui/icon';
import { Input, InputField } from '@/components/ui/input';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useMicroSurvey } from '../hooks/useMicroSurvey';

/**
 * MicroSurveyPopup
 *
 * A small pop-up that slides up from the bottom-right corner (web) or
 * bottom of the screen (mobile) to ask a quick one-tap question.
 * Mounted globally in the root layout.
 */
export function MicroSurveyPopup() {
  const { survey, visible, showThankYou, dismissThankYou, submit, close } = useMicroSurvey();

  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  const [additionalInfo, setAdditionalInfo] = useState('');

  const [showAdditionalInput, setShowAdditionalInput] = useState(false);

  const additionalPromptRef = useRef<string | undefined>(undefined);

  const additionalPlaceholderRef = useRef<string | undefined>(undefined);

  const { width } = useWindowDimensions();

  const isMobile = !isWeb || width < 768;

  useEffect(() => {
    if (visible) {
      setSelectedOption(null);

      setAdditionalInfo('');

      setShowAdditionalInput(false);

      additionalPromptRef.current = undefined;

      additionalPlaceholderRef.current = undefined;
    }
  }, [visible]);

  const handleOptionPress = useCallback(
    (value: string) => {
      if (!survey) {
        return;
      }

      const option = survey.options.find((o) => o.value === value);

      if (option?.additionalInfoPrompt) {
        setSelectedOption(value);

        setShowAdditionalInput(true);

        additionalPromptRef.current = option.additionalInfoPrompt;

        additionalPlaceholderRef.current = option.additionalInfoPlaceholder;
      } else {
        submit(value);
      }
    },
    [survey, submit]
  );

  const handleBack = useCallback(() => {
    setShowAdditionalInput(false);

    setSelectedOption(null);

    setAdditionalInfo('');
  }, []);

  const handleAdditionalSubmit = useCallback(() => {
    if (!selectedOption) {
      return;
    }

    submit(selectedOption, additionalInfo.trim() || null);
  }, [selectedOption, additionalInfo, submit]);

  if (!visible && !showThankYou) {
    return null;
  }

  if (showThankYou) {
    return (
      <Animated.View
        key="thank-you"
        entering={SlideInDown.delay(400).duration(400).springify()}
        exiting={SlideOutDown.duration(300)}
        style={[
          {
            position: 'absolute',
            zIndex: 100,
            ...(isMobile
              ? { bottom: 16, left: 16, right: 16 }
              : { bottom: 24, right: 24, width: 200 }),
          },
        ]}>
        <Pressable onPress={dismissThankYou}>
          <Box
            className="rounded-xl border-2 border-success-300 bg-background-success px-4 py-5 shadow-hard-5"
            style={{ boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)' }}>
            <Text className="text-center font-semibold text-sm text-success-700">Thank you!</Text>
          </Box>
        </Pressable>
      </Animated.View>
    );
  }

  if (!survey) {
    return null;
  }

  return (
    <Animated.View
      key="survey"
      entering={SlideInDown.duration(400).springify()}
      exiting={SlideOutDown.duration(300)}
      style={[
        {
          position: 'absolute',
          zIndex: 100,
          ...(isMobile
            ? { bottom: 16, left: 16, right: 16 }
            : { bottom: 24, right: 24, width: 360 }),
        },
      ]}>
      <Box
        className="rounded-xl border-2 border-outline-300 bg-background-0 p-4 shadow-hard-5"
        style={{ boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)' }}>
        <Pressable
          onPress={close}
          className="absolute right-3 top-3 z-10 rounded-full p-1"
          accessibilityLabel="Close survey"
          accessibilityRole="button">
          <Icon as={X} size="sm" className="text-typography-400" />
        </Pressable>
        <VStack space="md">
          <Heading size="sm" className="pr-6">
            {survey.question}
          </Heading>
          {!showAdditionalInput ? (
            <VStack space="sm">
              {survey.options.map((option) => (
                <Button
                  key={option.value}
                  size="sm"
                  className="bg-primary-500 data-[hover=true]:bg-primary-600 data-[active=true]:bg-primary-700"
                  onPress={() => handleOptionPress(option.value)}>
                  <ButtonText className="text-sm text-typography-0">{option.label}</ButtonText>
                </Button>
              ))}
            </VStack>
          ) : (
            <Animated.View entering={FadeIn.duration(200)} exiting={FadeOut.duration(150)}>
              <VStack space="sm">
                <Pressable
                  onPress={handleBack}
                  className="flex-row items-center gap-1"
                  accessibilityLabel="Back to options"
                  accessibilityRole="button">
                  <Icon as={ArrowLeft} size="xs" className="text-typography-500" />
                  <Text size="xs" className="text-typography-500">
                    Back
                  </Text>
                </Pressable>
                <Box className="rounded-lg bg-primary-50 px-3 py-2">
                  <Text size="sm" className="font-medium text-primary-700">
                    {selectedOption}
                  </Text>
                </Box>
                <Text size="xs" className="text-typography-500">
                  {additionalPromptRef.current} (optional)
                </Text>
                <Input size="sm">
                  <InputField
                    placeholder={additionalPlaceholderRef.current ?? ''}
                    value={additionalInfo}
                    onChangeText={setAdditionalInfo}
                    autoFocus
                    returnKeyType="done"
                    onSubmitEditing={handleAdditionalSubmit}
                  />
                </Input>
                <Button
                  size="sm"
                  className="bg-primary-500 data-[hover=true]:bg-primary-600 data-[active=true]:bg-primary-700"
                  onPress={handleAdditionalSubmit}>
                  <ButtonText className="text-sm text-typography-0">Submit</ButtonText>
                </Button>
              </VStack>
            </Animated.View>
          )}
        </VStack>
      </Box>
    </Animated.View>
  );
}
